import React, { useState } from 'react';

interface ShareButtonProps {
  /** Absolute URL (or relative — we'll promote to absolute) being shared. */
  url: string;
  /** Headline shown by the OS share sheet. */
  title?: string;
  /** Body text preview (appended with URL on platforms that support both). */
  text?: string;
  /** Optional class name for layout-specific styling. */
  className?: string;
  /** Label override — default "Share". */
  label?: string;
  /** Render only the icon (no visible label). The label is still used as
      aria-label + title so the button stays accessible. Used in tight
      mobile surfaces like the past-exam result row. */
  iconOnly?: boolean;
}

const abs = (url: string) =>
  url.startsWith('http') ? url : `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;

/**
 * Thin wrapper around navigator.share with a clipboard fallback. When the
 * browser doesn't expose Web Share (most desktops), we copy the URL and flash
 * a "Copied" state so the user can paste it into wherever they want.
 */
const ShareButton: React.FC<ShareButtonProps> = ({
  url,
  title,
  text,
  className,
  label = 'Share',
  iconOnly = false,
}) => {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const absUrl = abs(url);
    const shareData: ShareData = {
      url: absUrl,
      ...(title ? { title } : {}),
      ...(text ? { text } : {}),
    };

    const nav: any = typeof navigator !== 'undefined' ? navigator : null;
    if (nav?.share) {
      try {
        await nav.share(shareData);
        return;
      } catch (err: any) {
        // User dismissed — no-op. Anything else falls through to clipboard.
        if (err?.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(absUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt('Copy this link:', absUrl);
    }
  };

  return (
    <button
      type="button"
      className={className || 'share-btn'}
      onClick={handleShare}
      aria-label={copied ? 'Link copied' : label}
      title={copied ? 'Link copied' : label}
    >
      {copied ? (iconOnly ? '✓' : '✓ Copied') : (iconOnly ? '↗' : `↗ ${label}`)}
    </button>
  );
};

export default ShareButton;
